import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton } from '@mui/material';
import { CerebroDrawer, CerebroHeading, CerebroSecondaryButton } from '../../../shared/components';
import { useTermSheetFacilityCollateralQuery } from '../../../shared/hooks';
import { TermSheetFacility, TermSheetFacilityCollateral } from '../../../shared/models';
import { EnumUtils, NumberUtils } from '../../../shared/utils';
import { CollateralFormDialog } from './CollateralFormDialog';
import { DeleteCollateralDialog } from './DeleteCollateralDialog';
import { CollateralActionButton } from './CollateralActionButton';
import { EmptyGuid } from '../../../shared/constants';
import React from 'react';

interface FacilityCollateralDrawerProps {
	dealGuid: string | undefined;
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	close: () => void;
}

export const FacilityCollateralDrawer: React.FC<FacilityCollateralDrawerProps> = ({
	dealGuid,
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	close,
}) => {
	const [collateralForm, setCollateralForm] = React.useState<TermSheetFacilityCollateral | null>(null);
	const [collateralDelete, setCollateralDelete] = React.useState<TermSheetFacilityCollateral | null>(null);

	const collateralQuery = useTermSheetFacilityCollateralQuery(
		dataRoomGuid,
		termSheetGuid,
		revisionGuid,
		facility?.guid,
		{
			enabled: facility != null,
			refetchOnWindowFocus: false,
		}
	);
	const isFetching = collateralQuery.isLoading || collateralQuery.isFetching;

	const onCloseDialogs = () => {
		setCollateralForm(null);
		setCollateralDelete(null);
	};

	const createNewCollateral = () => {
		setCollateralForm({
			id: 0,
			guid: EmptyGuid,
			availabilityLimitType: 'None',
			collateralSecurityType: 'Unsecured',
			advancedRate: 0,
		});
	};

	if (collateralQuery.isFetching) {
		return (
			<CerebroDrawer open={facility != null} onClose={close}>
				<CerebroHeading>Collateral</CerebroHeading>
				<Skeleton height={60} width={600} />
				<Skeleton height={60} width={600} />
				<Skeleton height={60} width={600} />
			</CerebroDrawer>
		);
	}

	return (
		<CerebroDrawer open={facility != null} onClose={close}>
			<CerebroHeading>Update Facility Collateral</CerebroHeading>

			<CerebroSecondaryButton onClick={() => createNewCollateral()} sx={{ marginBottom: 2, marginTop: 3 }}>
				Add Collateral
			</CerebroSecondaryButton>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Type</TableCell>
							<TableCell>Advance Rate</TableCell>
							<TableCell>Availability Limit</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<TableRow>
								<TableCell colSpan={4}>
									<Skeleton height={60} />
								</TableCell>
							</TableRow>
						)}

						{collateralQuery.isSuccess && (
							<>
								{collateralQuery.data.map(collateral => {
									return (
										<TableRow key={collateral.guid}>
											<TableCell>
												{EnumUtils.GetCollateralSecurityTypeDescription(
													collateral.collateralSecurityType
												)}
											</TableCell>
											<TableCell>
												{collateral.availabilityLimitType == 'Custom' ? (
													<>{collateral.otherAvailabilityLimit}</>
												) : (
													<>
														{isCurrency(collateral) ? (
															NumberUtils.FormatCurrency(collateral.advancedRate)
														) : (
															<>{collateral.advancedRate}%</>
														)}
													</>
												)}
											</TableCell>
											<TableCell>
												{EnumUtils.GetFacilityAvailabilityLimitTypeDescription(
													collateral.availabilityLimitType
												)}
											</TableCell>
											<TableCell>
												<CollateralActionButton
													collateral={collateral}
													onEdit={setCollateralForm}
													onDelete={setCollateralDelete}
												/>
											</TableCell>
										</TableRow>
									);
								})}
								{collateralQuery.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={4}>
											No collateral currently associated to this facility.
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<CollateralFormDialog
				dealGuid={dealGuid}
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facility}
				collateral={collateralForm}
				close={onCloseDialogs}
			/>

			<DeleteCollateralDialog
				dealGuid={dealGuid}
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facility}
				collateral={collateralDelete}
				close={onCloseDialogs}
			/>
		</CerebroDrawer>
	);
};

function isCurrency(value: TermSheetFacilityCollateral): boolean {
	return value.availabilityLimitType == 'None' || value.availabilityLimitType == 'Limit';
}
